// src/pages/Users.jsx
import React, { useEffect, useRef, useState, useMemo } from "react";
import { Form } from "react-bootstrap";
import { Card, CardBody } from "./../../components/card/card.jsx";
import Chart from "react-apexcharts";
import Select from "react-select";
import DatePicker from "react-datepicker";
import { useNavigate, useSearchParams } from "react-router-dom";
import DashUserRow from "../../components/lists/DashUserRow.jsx";

import {
  fetchUserPageSummaryInfo,
  fetchUserPageDataPaginated,
  setUsersPageCurrent,
  setUsersSearchTerm,
  setUsersUserType,
  setUsersDateOption,
  setUsersAgeOption,
  setUsersCustomDateRange,
  setUsersStartDate,
  setUsersEndDate,
} from "../../actions/users_actions.js";
import Pagination from "./../../components/pagination/Pagination.jsx";
import { useDispatch, useSelector } from "react-redux";
import { dateOptions, AgeOptions } from "../../components/selectvalues/selectvalue.jsx";

const userTypeOptions = [
  { value: 0, label: "Registered Users" },
  { value: 1, label: "Active Users" },
];

const Users = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // ──────────────────────────────────────────────────────────────
  // Redux state
  // ──────────────────────────────────────────────────────────────
  const {
    users_date_option,
    users_custom_date_range,
    users_start_date,
    users_end_date,
    users_age_option,
    users_user_type,
    users_search_term,
    users_page_current,
    users_items_per_page,
  } = useSelector((state) => ({
    users_date_option: state.users.users_date_option,
    users_custom_date_range: state.users.users_custom_date_range,
    users_start_date: state.users.users_start_date,
    users_end_date: state.users.users_end_date,
    users_age_option: state.users.users_age_option,
    users_user_type: state.users.users_user_type,
    users_search_term: state.users.users_search_term,
    users_page_current: state.users.users_page_current,
    users_items_per_page: state.users.users_items_per_page,
  }));

  const pageUserSummaryInfo = useSelector((state) => state.users.page_user_summary_info);
  const loadingUserSummary = useSelector((state) => state.users.loading_user_summary);
  const loadingPaginated = useSelector((state) => state.users.loading_users_paginated);
  const paginatedData = useSelector((state) => state.users.users_paginated_info) || {};
  const paginatedUserList = paginatedData.userdata || [];
  const paginatedUserTotal = paginatedData.totalRegisterUser || 0;

  // Map Redux → full option objects
  const selectedDateOptionFromRedux = dateOptions.find((opt) => opt.value === users_date_option) || dateOptions[1];
  const selectedAgeOptionFromRedux  = AgeOptions.find((opt) => opt.value === users_age_option) || AgeOptions[0];
  const selectedUserTypeFromRedux   = userTypeOptions.find((opt) => opt.value === users_user_type) || userTypeOptions[0];

  // ──────────────────────────────────────────────────────────────
  // Local mirror of all filters (apply-on-click)
  // ──────────────────────────────────────────────────────────────
  const [localDateOption, setLocalDateOption]     = useState(selectedDateOptionFromRedux);
  const [localAgeOption, setLocalAgeOption]       = useState(selectedAgeOptionFromRedux);
  const [localUserType, setLocalUserType]         = useState(selectedUserTypeFromRedux);
  const [localSearchTerm, setLocalSearchTerm]     = useState(users_search_term || "");
  const [localCustomFrom, setLocalCustomFrom]     = useState(users_start_date ? new Date(users_start_date) : new Date());
  const [localCustomTo, setLocalCustomTo]         = useState(users_end_date ? new Date(users_end_date) : new Date());

  // keep locals in sync if Redux changes externally (e.g., nav)
  useEffect(() => setLocalDateOption(selectedDateOptionFromRedux), [selectedDateOptionFromRedux]);
  useEffect(() => setLocalAgeOption(selectedAgeOptionFromRedux), [selectedAgeOptionFromRedux]);
  useEffect(() => setLocalUserType(selectedUserTypeFromRedux), [selectedUserTypeFromRedux]);
  useEffect(() => setLocalSearchTerm(users_search_term || ""), [users_search_term]);
  useEffect(() => setLocalCustomFrom(users_start_date ? new Date(users_start_date) : new Date()), [users_start_date]);
  useEffect(() => setLocalCustomTo(users_end_date ? new Date(users_end_date) : new Date()), [users_end_date]);

  // equality helpers
  const eqOpt = (a, b) => (a?.value ?? null) === (b?.value ?? null);
  const eqDate = (a, b) => {
    if (!a && !b) return true;
    if (!a || !b) return false;
    return new Date(a).getTime() === new Date(b).getTime();
  };

  // detect if local filters equal committed Redux filters
  const filtersUnchanged = useMemo(() => {
    const localTrim  = (localSearchTerm || "").trim();
    const reduxTrim  = (users_search_term || "").trim();

    const sameDateOpt   = eqOpt(localDateOption, selectedDateOptionFromRedux);
    const sameAgeOpt    = eqOpt(localAgeOption, selectedAgeOptionFromRedux);
    const sameUserType  = eqOpt(localUserType, selectedUserTypeFromRedux);
    const sameSearch    = localTrim === reduxTrim;

    // if not "Custom", dates are irrelevant
    const sameDates =
      localDateOption?.value !== 3
        ? true
        : eqDate(localCustomFrom, users_start_date) &&
          eqDate(localCustomTo, users_end_date);

    return sameDateOpt && sameAgeOpt && sameUserType && sameSearch && sameDates;
  }, [
    localDateOption,
    localAgeOption,
    localUserType,
    localSearchTerm,
    localCustomFrom,
    localCustomTo,
    selectedDateOptionFromRedux,
    selectedAgeOptionFromRedux,
    selectedUserTypeFromRedux,
    users_search_term,
    users_start_date,
    users_end_date
  ]);


  // Apply state to hide chart and avoid wiggle
  const [isApplying, setIsApplying] = useState(false);
  const pendingApplyRef = useRef(null);

  // Combined loading flag (summary or table)
  const overallLoading = loadingUserSummary || loadingPaginated;
  const prevOverallLoading = useRef(overallLoading);
  useEffect(() => {
    if (prevOverallLoading.current && !overallLoading) {
      setIsApplying(false);
      pendingApplyRef.current = null;
    }
    prevOverallLoading.current = overallLoading;
  }, [overallLoading]);

  // URL ?page= sync → Redux
  useEffect(() => {
    const pageParam = parseInt(searchParams.get("page") || "", 10);
    if (pageParam && pageParam !== users_page_current) {
      dispatch(setUsersPageCurrent(pageParam));
    }
  }, [searchParams, users_page_current, dispatch]);

  // Build API params from Redux (committed) values
  const getSearchParamsObj = () => {
    let startTimeParam = "2010-01-01T00:00:00";
    let endTimeParam   = "2026-01-01T23:59:59";
    if (users_date_option === 3 && users_start_date && users_end_date) {
      startTimeParam = new Date(users_start_date).toISOString();
      endTimeParam   = new Date(users_end_date).toISOString();
    }

    const filterParam     = users_user_type.toString();
    const datefilterParam = users_date_option;
    const countryParam    = "0";

    let ageStartParam = "0";
    let ageEndParam   = "150";
    if (users_age_option !== "0") {
      const [endAge, startAge] = users_age_option.split("-");
      ageStartParam = startAge;
      ageEndParam   = endAge;
    }

    return {
      startTimeParam,
      endTimeParam,
      filterParam,
      datefilterParam,
      countryParam,
      ageStartParam,
      ageEndParam,
    };
  };

  // Fetch whenever committed Redux filters/page change
  useEffect(() => {
    const {
      startTimeParam,
      endTimeParam,
      filterParam,
      datefilterParam,
      countryParam,
      ageStartParam,
      ageEndParam,
    } = getSearchParamsObj();

    // Summary (chart)
    dispatch(
      fetchUserPageSummaryInfo(
        startTimeParam,
        endTimeParam,
        filterParam,
        datefilterParam,
        countryParam,
        ageStartParam,
        ageEndParam
      )
    );

    // Paginated table
    const trimmed = (users_search_term || "").trim();
    dispatch(
      fetchUserPageDataPaginated(
        startTimeParam,
        endTimeParam,
        filterParam,
        datefilterParam,
        countryParam,
        ageStartParam,
        ageEndParam,
        users_page_current,
        users_items_per_page,
        trimmed.length >= 3 ? trimmed : null
      )
    );
  }, [
    dispatch,
    users_date_option,
    users_custom_date_range,
    users_start_date,
    users_end_date,
    users_age_option,
    users_user_type,
    users_search_term,
    users_page_current,
    users_items_per_page,
  ]);

  // APPLY: commit locals → Redux, then effects above fetch; chart/table will be frozen
  const handleApplyAll = () => {
    // if nothing changed, do nothing — prevents “forever spinner”
    if (filtersUnchanged) return;

    pendingApplyRef.current = {
      dateOption: localDateOption,
      ageOption:  localAgeOption,
      userType:   localUserType,
      searchTerm: localSearchTerm,
      customFrom: localCustomFrom,
      customTo:   localCustomTo,
    };
    setIsApplying(true);
  };


  // after spinner is visible, commit to Redux (post-paint)
  useEffect(() => {
    if (!isApplying || !pendingApplyRef.current) return;

    const {
      dateOption,
      ageOption,
      userType,
      searchTerm,
      customFrom,
      customTo,
    } = pendingApplyRef.current;

    // Reset page to 1 & update URL
    dispatch(setUsersPageCurrent(1));
    navigate("/users?page=1", { replace: false });

    // Commit filters
    dispatch(setUsersDateOption(dateOption.value));
    dispatch(setUsersCustomDateRange(dateOption.value === 3));
    if (dateOption.value === 3) {
      dispatch(setUsersStartDate(customFrom));
      dispatch(setUsersEndDate(customTo));
    } else {
      dispatch(setUsersStartDate(null));
      dispatch(setUsersEndDate(null));
    }

    dispatch(setUsersAgeOption(ageOption.value));
    dispatch(setUsersUserType(userType.value));
    dispatch(setUsersSearchTerm(searchTerm));

    // fetching is handled by the big useEffect above
  }, [isApplying, dispatch, navigate]);

  // Pagination click (unchanged)
  const paginate = (pageNumber) => {
    dispatch(setUsersPageCurrent(pageNumber));
    navigate(`/users?page=${pageNumber}`, { replace: false });

    const trimmed = (users_search_term || "").trim();
    const {
      startTimeParam,
      endTimeParam,
      filterParam,
      datefilterParam,
      countryParam,
      ageStartParam,
      ageEndParam,
    } = getSearchParamsObj();

    dispatch(
      fetchUserPageDataPaginated(
        startTimeParam,
        endTimeParam,
        filterParam,
        datefilterParam,
        countryParam,
        ageStartParam,
        ageEndParam,
        pageNumber,
        users_items_per_page,
        trimmed.length >= 3 ? trimmed : null
      )
    );
  };

  // Showing range
  const startIndex = paginatedUserTotal
    ? (users_page_current - 1) * users_items_per_page + 1
    : 0;
  const endIndex = Math.min(users_page_current * users_items_per_page, paginatedUserTotal);
  const themeColor = getComputedStyle(document.body).getPropertyValue("--bs-theme").trim();

  // ──────────────────────────────────────────────────────────────
  // Chart data (memoized) + no-wiggle options while applying/loading
  // ──────────────────────────────────────────────────────────────
  const summaryRows = pageUserSummaryInfo?.dateswise || [];
  const userlabels = useMemo(() => summaryRows.map((item) => item.label), [summaryRows]);
  const userlinevalues = useMemo(() => summaryRows.map((item) => item.value), [summaryRows]);

  const yLabelColors = useMemo(() => Array(userlinevalues.length).fill(themeColor), [userlinevalues, themeColor]);

  const baseChartOptions = useMemo(() => ({
    chart: {
      foreColor: themeColor,
      shadow: { enabled: true, color: "rgba(255,255,255, .5)", top: 18, left: 7, blur: 10, opacity: 1 },
      toolbar: { show: false },
    },
    colors: [themeColor, "#777"],
    dataLabels: { enabled: false },
    stroke: { curve: "straight", width: 3 },
    grid: { borderColor: "#ccc" },
    markers: { size: 6 },
    xaxis: { categories: userlabels, labels: { style: { fontSize: "12px", colors: [themeColor] } } },
    yaxis: { labels: { style: { fontSize: "12px", colors: yLabelColors } } },
    legend: { show: true },
  }), [themeColor, userlabels, yLabelColors]);

  const hideChart = isApplying || loadingUserSummary;
  const chartOptions = useMemo(() => {
    const base = baseChartOptions;
    return {
      ...base,
      chart: {
        ...base.chart,
        animations: { enabled: !hideChart },
        redrawOnParentResize: false,
        redrawOnWindowResize: false,
      },
      xaxis: {
        ...(base.xaxis || {}),
        categories: userlabels, // keep identity tied to data
      },
    };
  }, [baseChartOptions, hideChart, userlabels]);

  const chartSeries = useMemo(
    () => [{ name: "Users", data: userlinevalues }],
    [userlinevalues]
  );

  // Freeze Apex updates while hidden (optional but helpful)
  const FrozenChart = useMemo(
    () =>
      React.memo(
        function Inner({ options, series, type, height, freeze }) {
          return <Chart options={options} series={series} type={type} height={height} />;
        },
        (prev, next) => {
          if (next.freeze) return true;
          return (
            prev.options === next.options &&
            prev.series === next.series &&
            prev.type === next.type &&
            prev.height === next.height
          );
        }
      ),
    []
  );

  return (
    <>
      <h6>
        <span style={{ color: themeColor }}>PAGES</span> / USERS
      </h6>
      <div className="row">
        <div className="col-xl-8">
          <h3>USERS</h3>
        </div>
      </div>
      <div className="d-flex align-items-center users-total-label mb-2">
        <h5 className="me-2 mb-0 fw-bold">Total:</h5>
        <h5 className="mb-0" style={{ color: themeColor }}>{pageUserSummaryInfo?.totalRegisterUser || 0}</h5>
      </div>

      {/* Filters (centered, apply-on-click) */}
      <Card>
        <CardBody>
          <div className="d-flex justify-content-center align-items-end flex-wrap gap-3 mb-3">
            <div style={{ minWidth: 220 }}>
              <label>Time Interval</label>
              <Select
                options={dateOptions}
                value={localDateOption}
                onChange={setLocalDateOption}
                classNamePrefix="react-select"
              />
            </div>

            <div style={{ minWidth: 220 }}>
              <label>Age Groups</label>
              <Select
                options={AgeOptions}
                value={localAgeOption}
                onChange={setLocalAgeOption}
                classNamePrefix="react-select"
              />
            </div>

            <div style={{ minWidth: 220 }}>
              <label>User Type</label>
              <Select
                options={userTypeOptions}
                value={localUserType}
                onChange={setLocalUserType}
                classNamePrefix="react-select"
              />
            </div>

            <div style={{ minWidth: 360 }}>
              <label>Search Term</label>
              <Form.Control
                type="text"
                value={localSearchTerm}
                onChange={(e) => setLocalSearchTerm(e.target.value)}
                placeholder="Search Users (minimum 3 characters)"
              />
            </div>

            {/* Separate apply button (not stuck to the input) */}
            <div className="d-flex align-items-end">
              <button className="btn btn-secondary" onClick={handleApplyAll} type="button">
                <i className="fas fa-search"></i>
              </button>
            </div>
          </div>


          {/* Custom dates (only when "Custom") */}
          {localDateOption?.value === 3 && (
            <div className="d-flex justify-content-center flex-wrap gap-3 mb-3">
              <div style={{ minWidth: 220 }}>
                <label>Date From:</label>
                <DatePicker selected={localCustomFrom} onChange={setLocalCustomFrom} className="form-control" />
              </div>
              <div style={{ minWidth: 220 }}>
                <label>Date To:</label>
                <DatePicker selected={localCustomTo} onChange={setLocalCustomTo} className="form-control" />
              </div>
            </div>
          )}

          {/* Chart (frozen during apply/load; spinner overlay) */}
          <div className="col-lg-12" style={{ height: "350px" }}>
            <div className="position-relative" style={{ height: "100%" }}>
              <div style={{ visibility: hideChart ? "hidden" : "visible", height: "100%" }}>
                {chartSeries[0].data.length ? (
                  <FrozenChart
                    freeze={hideChart}
                    options={chartOptions}
                    series={chartSeries}
                    type="line"
                    height={350}
                  />
                ) : (
                  <div
                    style={{
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#888",
                    }}
                  >
                    No data
                  </div>
                )}
              </div>

              {hideChart && (
                <div
                  className="position-absolute top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center"
                  style={{ background: "rgba(0,0,0,0.0)" }}
                >
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Paginated Table */}
      <div className="col-xl-12">
        <Card className="mt-3">
          <span style={{ marginTop: "10px", left: "20px" }}>USER DETAILS</span>
          <CardBody>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginBottom: "10px",
                fontSize: "14px",
                color: "#fff",
              }}
            >
              {loadingPaginated ? (
                <small style={{ color: themeColor }}>Loading users...</small>
              ) : (
                <small>
                  <span style={{ color: "lightgray" }}>Showing</span>{" "}
                  <span style={{ color: themeColor }}>{startIndex}</span>
                  <span style={{ color: "lightgray" }}>{" "}-{" "}</span>
                  <span style={{ color: themeColor }}>{endIndex}</span>{" "}
                  <span style={{ color: "lightgray" }}>of{" "}</span>
                  <span style={{ color: themeColor }}>{paginatedUserTotal}</span> users
                </small>
              )}
            </div>
            <div
              className="table-responsive"
              style={{
                minHeight: "300px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {loadingPaginated ? (
                <div className="spinner-border" role="status" style={{ color: themeColor }}>
                  <span className="visually-hidden">Loading...</span>
                </div>
              ) : paginatedUserList.length === 0 ? (
                <div>No Records Found</div>
              ) : (
                <table className="table table-bordered">
                  <tbody>
                    {paginatedUserList.map((user, index) => (
                      <DashUserRow
                        key={user.id || user.userId || user._id || index}
                        user={user}
                        rank={(users_page_current - 1) * users_items_per_page + index + 1}
                        themeColor={themeColor}
                      />
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </CardBody>
        </Card>
        <div className="row mt-3">
          <div className="col-md-10"></div>
          <div className="col-md-2">
            <Pagination
              currentPage={users_page_current}
              itemsPerPage={users_items_per_page}
              totalItems={paginatedUserTotal}
              paginate={paginate}
            />
          </div>
        </div>
      </div>

      <style>{`
        .users-total-label h5 {
          color: #2d3748;
          transition: color .2s;
        }
        @media (prefers-color-scheme: dark) {
          .users-total-label h5 { color: #fff; }
        }
        .user-name { color: #2d3748; font-weight: 600; transition: color .2s; }
        @media (prefers-color-scheme: dark) {
          .user-name { color: #fff; }
        }
      `}</style>
    </>
  );
};

export default Users;
